import React, { ChangeEvent, FormEvent, useState } from 'react';
import type { GlueVariable } from '@glueapp/graph-types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VariableTypeInput } from './VariableTypeInput';

interface Props {
    variables: GlueVariable[];
    selectedVariable?: GlueVariable;
    setSelectedVariable: (name: string | undefined) => void;
    addVariable: (newVariable: GlueVariable) => void;
    updateVariable: (oldName: string, variable: GlueVariable) => void;
    deleteVariable: (name: string) => void;
}

export const VariableEditor: React.FC<Props> = ({
    variables,
    selectedVariable,
    setSelectedVariable,
    addVariable,
    updateVariable,
    deleteVariable,
}) => {
    const [editVariableDraft, setEditVariableDraft] = useState<GlueVariable | undefined>(undefined);
    const [newVariableDraft, setNewVariableDraft] = useState<GlueVariable | undefined>(undefined);

    const renderCurrentVariableSection = () => {
        if (selectedVariable && editVariableDraft) {
            const handleSubmit = (e: FormEvent) => {
                e.preventDefault();
                if (editVariableDraft.name.length < 1) {
                    return;
                }

                updateVariable(selectedVariable.name, editVariableDraft);
                setEditVariableDraft(undefined);
            };

            const createButtonDisabled = editVariableDraft.name.length < 1;
            return (
                <div className="rounded-md border border-border bg-muted/50 p-3 mb-2">
                    <label className="text-xs font-medium text-muted-foreground mb-2 block">Edit variable: {selectedVariable.name}</label>
                    <Input
                        placeholder="Variable Name"
                        value={editVariableDraft.name}
                        onChange={e => setEditVariableDraft({ ...editVariableDraft, name: e.target.value })}
                    />
                    <VariableTypeInput
                        dataType={editVariableDraft.type}
                        typeDetermination={editVariableDraft.typeDetermination}
                        onChange={(typeDetermination, dataType) =>
                            setEditVariableDraft({ ...editVariableDraft, typeDetermination, type: dataType })
                        }
                    />
                    <div className="mt-3 flex flex-row justify-between gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditVariableDraft(undefined)}>
                            Discard
                        </Button>
                        <Button size="sm" disabled={createButtonDisabled} onClick={handleSubmit}>
                            Update
                        </Button>
                    </div>
                </div>
            );
        } else {
            const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
                const newSelectedVariable = variables.find(variable => variable.name === event.target.value);
                if (newSelectedVariable) {
                    setSelectedVariable(newSelectedVariable.name);
                }
            };

            return (
                <div className="flex flex-row gap-2">
                    <Select
                        disabled={variables.length === 0}
                        value={selectedVariable?.name}
                        onValueChange={value => handleSelect({ target: { value } } as ChangeEvent<HTMLSelectElement>)}
                    >
                        <SelectTrigger className="flex-1">
                            <SelectValue placeholder={variables.length === 0 ? 'No variables created yet' : 'Select a variable'} />
                        </SelectTrigger>
                        <SelectContent>
                            {variables.map(variable => {
                                return (
                                    <SelectItem value={variable.name} key={variable.name}>
                                        {variable.name}
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" disabled={!selectedVariable} onClick={() => setEditVariableDraft(selectedVariable)}>
                        Edit
                    </Button>
                </div>
            );
        }
    };

    const renderCreateVariableForm = () => {
        if (newVariableDraft) {
            const handleSubmit = (e: FormEvent) => {
                e.preventDefault();
                if (newVariableDraft.name.length < 1 || variables.find(variable => variable.name === newVariableDraft.name)) {
                    return;
                }

                addVariable(newVariableDraft);
                setSelectedVariable(newVariableDraft.name);
                setNewVariableDraft(undefined);
            };

            const createButtonDisabled = newVariableDraft.name.length < 1;
            return (
                <div className="rounded-md border border-border bg-muted/50 p-3">
                    <label className="text-xs font-medium text-muted-foreground mb-2 block">New Variable</label>
                    <Input
                        placeholder="Variable Name"
                        value={newVariableDraft.name}
                        onChange={e => setNewVariableDraft({ ...newVariableDraft, name: e.target.value })}
                    />
                    <VariableTypeInput
                        dataType={newVariableDraft.type}
                        typeDetermination={newVariableDraft.typeDetermination}
                        onChange={(typeDetermination, dataType) =>
                            setNewVariableDraft({ ...newVariableDraft, typeDetermination, type: dataType })
                        }
                    />
                    <div className="mt-3 flex flex-row justify-between gap-2">
                        <Button variant="outline" size="sm" onClick={() => setNewVariableDraft(undefined)}>
                            Discard
                        </Button>
                        <Button size="sm" disabled={createButtonDisabled} onClick={handleSubmit}>
                            Create
                        </Button>
                    </div>
                </div>
            );
        } else {
            return (
                <Button variant="outline" onClick={() => setNewVariableDraft({ name: '', type: undefined, typeDetermination: 'INFERRED' })}>
                    New Variable
                </Button>
            );
        }
    };

    return (
        <div className="flex flex-col gap-4 pb-4">
            {newVariableDraft ? null : renderCurrentVariableSection()}
            <section>{renderCreateVariableForm()}</section>
        </div>
    );
};
